import { useState } from "react";
import {
    AlertTriangle,
    CheckCircle2,
    Loader2,
    Mail,
    Send,
} from "lucide-react";
import { adminApi } from "@/components/Admin/api";
import { formatRel } from "@/components/Admin/format";

export default function EmailPanel({
    stats,
    emailLog,
    onAfterTest,
}) {
    const [emailTo, setEmailTo] = useState("");
    const [result, setResult] = useState(null);
    const [sending, setSending] = useState(false);

    const sendTest = async () => {
        setSending(true);
        setResult(null);
        try {
            const body = emailTo.trim() ? { to_email: emailTo.trim() } : {};
            const r = await adminApi.post("/email/test", body);
            setResult(r.data);
            if (onAfterTest) await onAfterTest();
        } catch (err) {
            setResult({
                delivered: false,
                error: err?.message || "Test request failed",
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <section className="mt-12 grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                <div className="flex items-center justify-between">
                    <h3 className="font-display text-2xl leading-none">
                        Email lead routing
                    </h3>
                    <Mail className="w-4 h-4 text-[#1FA8FF]" />
                </div>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                    Every demo and capability submission is emailed to the
                    address below in addition to MongoDB and webhook dispatch.
                    Configure SMTP via{" "}
                    <code className="text-zinc-300">SMTP_HOST</code>,{" "}
                    <code className="text-zinc-300">SMTP_USER</code>,{" "}
                    <code className="text-zinc-300">SMTP_PASSWORD</code>,{" "}
                    <code className="text-zinc-300">SMTP_FROM</code> in backend
                    .env, then test below.
                </p>

                <div className="mt-5 space-y-2 text-sm">
                    <Row label="Destination" value={stats?.notify_email || "—"} />
                    <Row
                        label="SMTP host"
                        value={stats?.smtp_host || "Not configured"}
                    />
                    <Row label="From" value={stats?.smtp_from || "—"} />
                    <Row
                        label="Status"
                        value={
                            stats?.email_configured
                                ? "Routing on"
                                : "Not configured"
                        }
                        tone={stats?.email_configured ? "good" : "warn"}
                    />
                </div>

                <div className="mt-5 space-y-3">
                    <label className="flex flex-col gap-1.5">
                        <span className="text-[11px] uppercase tracking-widest text-zinc-400">
                            Override destination (optional)
                        </span>
                        <input
                            type="email"
                            value={emailTo}
                            onChange={(e) => setEmailTo(e.target.value)}
                            placeholder={
                                stats?.notify_email || "you@yourdomain.com"
                            }
                            className="px-3.5 py-3 rounded-lg border border-white/10 bg-[#0A0A0A] text-white placeholder-zinc-600 focus:border-[#1FA8FF] focus:ring-2 focus:ring-[#1FA8FF]/30 outline-none"
                            data-testid="email-to"
                        />
                    </label>
                    <button
                        onClick={sendTest}
                        disabled={sending}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#1FA8FF] text-white font-semibold hover:bg-[#0C84D4] disabled:opacity-60"
                        data-testid="email-test"
                    >
                        {sending ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Sending…
                            </>
                        ) : (
                            <>
                                <Send className="w-4 h-4" />
                                Send test email
                            </>
                        )}
                    </button>

                    {result && <ResultBanner result={result} />}
                </div>
            </div>

            <RecentEmails emailLog={emailLog} />
        </section>
    );
}

function RecentEmails({ emailLog }) {
    return (
        <div className="lg:col-span-3 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
            <h3 className="font-display text-2xl leading-none">Recent emails</h3>
            <p className="mt-2 text-sm text-zinc-400">
                Last 25 outbound email attempts (newest first).
            </p>
            <div className="mt-5 divide-y divide-white/5">
                {emailLog.length === 0 && (
                    <p className="py-10 text-center text-zinc-500 text-sm">
                        No emails sent yet.
                    </p>
                )}
                {emailLog.map((row) => (
                    <div key={row.id} className="py-3 flex items-start gap-4">
                        <span
                            className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${
                                row.delivered
                                    ? "bg-[#10B981]"
                                    : "bg-[#F59E0B]"
                            }`}
                        />
                        <div className="min-w-0 flex-1">
                            <div className="text-sm text-white truncate">
                                {row.subject}
                            </div>
                            <div className="text-xs text-zinc-500 truncate">
                                → {row.to}
                                {row.error ? ` · ${row.error}` : ""}
                            </div>
                        </div>
                        <div className="text-xs text-zinc-500 shrink-0">
                            {formatRel(row.at)}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Row({ label, value, tone }) {
    const toneCls =
        tone === "good"
            ? "text-[#10B981]"
            : tone === "warn"
              ? "text-[#F59E0B]"
              : "text-white";
    return (
        <div className="flex items-baseline gap-3">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 w-28 shrink-0">
                {label}
            </span>
            <span className={`text-[13px] truncate ${toneCls}`}>{value}</span>
        </div>
    );
}

function ResultBanner({ result }) {
    return (
        <div
            className={`rounded-lg p-3 border text-sm ${
                result.delivered
                    ? "border-[#10B981]/30 bg-[#10B981]/10"
                    : "border-[#F59E0B]/30 bg-[#F59E0B]/10"
            }`}
            data-testid="email-result"
        >
            <div className="flex items-center gap-2 font-semibold">
                {result.delivered ? (
                    <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                ) : (
                    <AlertTriangle className="w-4 h-4 text-[#F59E0B]" />
                )}
                {result.delivered ? "Delivered" : "Not delivered"}
            </div>
            {result.reason && (
                <p className="mt-1.5 text-zinc-400">{result.reason}</p>
            )}
            {result.error && (
                <p className="mt-1.5 text-zinc-400 break-words">
                    {result.error}
                </p>
            )}
            {result.to && (
                <p className="mt-1.5 text-zinc-500 truncate">→ {result.to}</p>
            )}
        </div>
    );
}
